import React, { FC, useState } from 'react'
import { Box, Button, Grid, Typography } from '@material-ui/core'
import { useFormik } from 'formik'
import { DayTypeEnum, IEarlyDiscount } from 'typescript/interfaces/deals'
import { MAX_VALUE_IS, MIN_VALUE_IS, NUMBERS_ONLY } from 'constants/errors'
import * as yup from 'yup'
import SwitchExtended from 'UI/Switch/SwitchExtended'
import DecimalTextField from 'UI/TextField/DecimalTextField'
import { DECIMAL_PLACES_PERCENTAGE } from 'constants/calcConstants'
import { convertToPriceFormat } from 'utils/formatters'

const getOptionSchema = () =>
  yup
    .number()
    .nullable()
    .notRequired()
    .typeError(NUMBERS_ONLY)
    .min(1, MIN_VALUE_IS.replace(':value', '1'))
    .max(2, MAX_VALUE_IS.replace(':value', '2'))

const getDaysSchema = () => {
  return yup
    .number()
    .nullable()
    .notRequired()
    .typeError(NUMBERS_ONLY)
    .min(1, MIN_VALUE_IS.replace(':value', '1'))
    .max(365, MAX_VALUE_IS.replace(':value', '365'))
}

const validationSchema = yup.object().shape({
  thirtyDays: getOptionSchema(),
  sixtyDays: getOptionSchema(),
  ninetyDays: getOptionSchema(),
  custom: getOptionSchema(),
  customDays: getDaysSchema(),
})

const defaultValues: IEarlyDiscountForm = {
  thirtyDays: undefined,
  sixtyDays: undefined,
  ninetyDays: undefined,
  custom: undefined,
  customDays: undefined,
}

interface IEarlyDiscountForm {
  thirtyDays?: number
  sixtyDays?: number
  ninetyDays?: number
  custom?: number
  customDays?: number
}

interface EarlyDiscountFormProps {
  initialValues?: IEarlyDiscount & { customDays?: number }
  onSubmit: (values?: IEarlyDiscount & { customDays?: number }) => void
  classes: any
  advanceAmount?: number
  disableStaticOptions?: boolean
}

const EarlyDiscountForm: FC<EarlyDiscountFormProps> = ({
  initialValues,
  onSubmit,
  advanceAmount,
  disableStaticOptions = false,
  ...props
}) => {
  const [dayType, setDayType] = useState<DayTypeEnum>(
    initialValues?.dayType ?? DayTypeEnum.calendar
  )

  let initialFormValues: IEarlyDiscountForm = defaultValues

  if (initialValues) {
    for (const [key, value] of Object.entries(initialValues)) {
      const newKey = key as keyof IEarlyDiscountForm

      if (newKey in defaultValues) {
        initialFormValues = {
          ...initialFormValues,
          [newKey]: value,
        }
      }
    }
  }

  const calculateAmount = (factor?: number): string => {
    if (!factor || !advanceAmount) return '-'
    return convertToPriceFormat(advanceAmount * factor)
  }

  const formatFactorValue = (value?: number | string): string => {
    if (value === undefined || value === null) return ''
    if (typeof value === 'string') return value
    return value.toFixed(4).replace(/\.?0+$/, '')
  }

  const { values, errors, touched, handleChange, handleSubmit } = useFormik<IEarlyDiscountForm>({
    validationSchema,
    initialValues: initialFormValues,
    enableReinitialize: false,
    onSubmit: formValues => {
      const transformedValues: any = Object.fromEntries(
        Object.entries(formValues).map(([key, value]) => [key, value === '' ? null : value])
      )
      if (Object.values(transformedValues).every(x => !x)) {
        onSubmit(undefined)

        return
      }

      const result: IEarlyDiscount & {
        customDays?: number
      } = {
        ...transformedValues,
        dayType,
      }

      onSubmit(result)
    },
  })

  return (
    <Box style={{ width: '100%', maxWidth: 'none' }}>
      <form onSubmit={handleSubmit} style={{ margin: 0, padding: 0 }}>
        <Box mb={1}>
          <Typography variant="body1">Early discounts</Typography>
        </Box>

        {dayType === DayTypeEnum.calendar ? (
          <SwitchExtended
            key={'calendar'}
            variants={[
              { title: 'Calendar Days', value: 'calendar' },
              { title: 'Business Days', value: 'business' },
            ]}
            value="calendar"
            onClick={() => {
              if (disableStaticOptions) return
              setDayType(DayTypeEnum.business)
            }}
          />
        ) : (
          <SwitchExtended
            key={'business'}
            variants={[
              { title: 'Calendar Days', value: 'calendar' },
              { title: 'Business Days', value: 'business' },
            ]}
            value="business"
            onClick={() => {
              if (disableStaticOptions) return
              setDayType(DayTypeEnum.calendar)
            }}
          />
        )}

        <Grid
          container
          style={{ marginTop: '0.75rem', maxWidth: 'none', width: '100%' }}
          spacing={0}
        >
          <Grid item xs={12} style={{ paddingTop: '0.5rem', paddingBottom: '0.25rem' }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              style={{ marginBottom: 2 }}
            >
              <Typography variant="body2" color="textSecondary" noWrap>
                Thirty (30) Days Option
              </Typography>
              <Typography variant="body2" color="textSecondary" noWrap>
                Amount
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <Box style={{ flex: '0 0 50%', marginRight: 2 }}>
                <DecimalTextField
                  decimalScale={DECIMAL_PLACES_PERCENTAGE}
                  {...props}
                  disabled={disableStaticOptions}
                  variant="outlined"
                  placeholder="Factor rate"
                  InputLabelProps={{ shrink: true }}
                  name="thirtyDays"
                  value={formatFactorValue(values.thirtyDays)}
                  onChange={handleChange}
                  error={Boolean(touched.thirtyDays && errors.thirtyDays)}
                  helperText={touched.thirtyDays && errors.thirtyDays}
                  style={{ marginBottom: 0, marginTop: 0 }}
                  FormHelperTextProps={{
                    style: { marginTop: 2 },
                  }}
                />
              </Box>
              <Typography
                variant="body1"
                style={{ flex: '0 0 50%', textAlign: 'right', fontSize: '0.875rem' }}
              >
                {calculateAmount(values.thirtyDays)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} style={{ paddingTop: '0.25rem', paddingBottom: '0.25rem' }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              style={{ marginBottom: 2 }}
            >
              <Typography variant="body2" color="textSecondary" noWrap>
                Sixty (60) Days Option
              </Typography>
              <Typography variant="body2" color="textSecondary" noWrap>
                Amount
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <Box style={{ flex: '0 0 50%', marginRight: 2 }}>
                <DecimalTextField
                  decimalScale={DECIMAL_PLACES_PERCENTAGE}
                  {...props}
                  disabled={disableStaticOptions}
                  variant="outlined"
                  placeholder="Factor rate"
                  InputLabelProps={{ shrink: true }}
                  name="sixtyDays"
                  value={formatFactorValue(values.sixtyDays)}
                  onChange={handleChange}
                  error={Boolean(touched.sixtyDays && errors.sixtyDays)}
                  helperText={touched.sixtyDays && errors.sixtyDays}
                  style={{ marginBottom: 0, marginTop: 0 }}
                  FormHelperTextProps={{
                    style: { marginTop: 2 },
                  }}
                />
              </Box>
              <Typography
                variant="body1"
                style={{ flex: '0 0 50%', textAlign: 'right', fontSize: '0.875rem' }}
              >
                {calculateAmount(values.sixtyDays)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} style={{ paddingTop: '0.25rem', paddingBottom: '0.25rem' }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              style={{ marginTop: 2, marginBottom: 0 }}
            >
              <Typography variant="body2" color="textSecondary" noWrap>
                Ninety (90) Days Option
              </Typography>
              <Typography variant="body2" color="textSecondary" noWrap>
                Amount
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" style={{ marginTop: 0, marginBottom: 2 }}>
              <Box style={{ flex: '0 0 50%', marginRight: 2 }}>
                <DecimalTextField
                  decimalScale={DECIMAL_PLACES_PERCENTAGE}
                  {...props}
                  disabled={disableStaticOptions}
                  variant="outlined"
                  placeholder="Factor rate"
                  InputLabelProps={{ shrink: true }}
                  name="ninetyDays"
                  value={formatFactorValue(values.ninetyDays)}
                  onChange={handleChange}
                  error={Boolean(touched.ninetyDays && errors.ninetyDays)}
                  helperText={touched.ninetyDays && errors.ninetyDays}
                  style={{ marginBottom: 0, marginTop: 0 }}
                  FormHelperTextProps={{
                    style: { marginTop: 0 },
                  }}
                />
              </Box>
              <Typography
                variant="body1"
                style={{ flex: '0 0 50%', textAlign: 'right', fontSize: '0.875rem' }}
              >
                {calculateAmount(values.ninetyDays)}
              </Typography>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            style={{
              paddingTop: '0.5rem',
              paddingBottom: '0.25rem',
            }}
          >
            <Box style={{ marginBottom: 2 }}>
              <Typography variant="body2" color="textSecondary" noWrap>
                Custom Option
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              style={{ marginBottom: 2 }}
            >
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ fontSize: '0.8rem' }}
                noWrap
              >
                Days
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ fontSize: '0.8rem' }}
                noWrap
              >
                Factor Rate
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ fontSize: '0.8rem' }}
                noWrap
              >
                Amount
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <Box style={{ flex: '0 0 33%', marginRight: 2 }}>
                <DecimalTextField
                  {...props}
                  variant="outlined"
                  placeholder="Days"
                  InputLabelProps={{ shrink: true }}
                  name="customDays"
                  value={values.customDays ?? ''}
                  onChange={handleChange}
                  error={Boolean(touched.customDays && errors.customDays)}
                  helperText={touched.customDays && errors.customDays}
                  style={{ marginBottom: 0, marginTop: 0 }}
                  FormHelperTextProps={{
                    style: { marginTop: 2 },
                  }}
                />
              </Box>
              <Box style={{ flex: '0 0 33%', marginRight: 2 }}>
                <DecimalTextField
                  decimalScale={DECIMAL_PLACES_PERCENTAGE}
                  {...props}
                  variant="outlined"
                  placeholder="Factor rate"
                  InputLabelProps={{ shrink: true }}
                  name="custom"
                  value={formatFactorValue(values.custom)}
                  onChange={handleChange}
                  error={Boolean(touched.custom && errors.custom)}
                  helperText={touched.custom && errors.custom}
                  style={{ marginBottom: 0, marginTop: 0 }}
                  FormHelperTextProps={{
                    style: { marginTop: 2 },
                  }}
                />
              </Box>
              <Typography
                variant="body1"
                style={{ flex: '0 0 34%', textAlign: 'right', fontSize: '0.875rem' }}
              >
                {calculateAmount(values.custom)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          fullWidth
          style={{ marginTop: '0.75rem' }}
        >
          Save
        </Button>
      </form>
    </Box>
  )
}

export default EarlyDiscountForm
